import { isComplianceReviewHoldStatus } from "../review-hold/utils";
import { CheckResult, FulfillmentNotes } from "../types";

export const CLIENT_VISIBLE_NOTE_STATUSES = [
  "IN_PROGRESS",
  "APPLICANT_ACTION_REQUIRED",
  "CLIENT_ACTION_REQUIRED",
  "SUBMITTED_TO_SOURCE",
  "COMPLETE",
  "CANCELLED",
] as const;

export const checkResultHasUiElementsOrMessages = (
  checkResult: CheckResult
) => {
  const reportElements: any[] =
    (checkResult.results_ui_schema.elements as any[] | undefined) ?? [];
  return Boolean(
    reportElements.length > 0 || checkResult.results_ui_schema.options
  );
};

/**
 * Determines if a check type should show data when in progress.
 * Returns true if the check type is allowed to display results during IN_PROGRESS status.
 *
 * @param checkType - The check type identifier (e.g., "DIGITAL_REFERENCE_CHECK_1")
 * @returns true if the check type should show data when in progress, false otherwise
 */
export const shouldShowDataWhenInProgress = (checkType: string): boolean => {
  // Map of check type patterns that should show data when in progress
  // Add or remove check types as needed
  const allowedCheckTypes = [
    "DIGITAL_REFERENCE_CHECK_1",
    "DIGITAL_REFERENCE_CHECKS_1",
    "PROFESSIONAL_REFERENCE_1",
    "PERSONAL_REFERENCE_1",
    "GAP_REFERENCE_1",
    "EMPLOYMENT_VERIFICATIONS_1",
    "EMPLOYMENT_VERIFICATION_MANUAL_1",
    "EMPLOYMENT_VERIFICATION_MANUAL_US_1",
    "EDUCATION_VERIFICATIONS_1",
    "EDUCATION_VERIFICATION_DIRECT_TO_SOURCE_1",
    "EDUCATION_VERIFICATION_CA_1",
    "EDUCATION_VERIFICATION_UK_1",
    "EDUCATION_VERIFICATION_US_1",
  ];

  return allowedCheckTypes.some((allowedType) => checkType === allowedType);
};

export function hasExternalFulfillmentNotes(
  fulfillmentNotes?: Pick<FulfillmentNotes, "notes">
): boolean {
  return Boolean(fulfillmentNotes?.notes?.length);
}

export function shouldOpenCheckResultOnPdf({
  status,
  hasResults,
  canShowInProgress,
  hasFulfillmentNotes,
}: {
  status: string;
  hasResults: boolean;
  canShowInProgress: boolean;
  hasFulfillmentNotes: boolean;
}): boolean {
  if (status === "COMPLETE" || isComplianceReviewHoldStatus(status)) {
    return true;
  }
  if (
    hasFulfillmentNotes &&
    (CLIENT_VISIBLE_NOTE_STATUSES as readonly string[]).includes(status)
  ) {
    return true;
  }
  if (!hasResults) {
    return false;
  }
  if (status === "CANCELLED") {
    return true;
  }
  if (status === "IN_PROGRESS" && canShowInProgress) {
    return true;
  }
  return false;
}

export function shouldShowCheckResultExpandIcon({
  status,
  hasResults,
  canShowInProgress,
  hasFulfillmentNotes,
  isPDF,
  isUmbrellaParent,
}: {
  status: string;
  hasResults: boolean;
  canShowInProgress: boolean;
  hasFulfillmentNotes: boolean;
  isPDF: boolean;
  isUmbrellaParent: boolean;
}): boolean {
  if (isPDF || isUmbrellaParent) {
    return false;
  }
  return (
    status === "COMPLETE" ||
    isComplianceReviewHoldStatus(status) ||
    (status === "CANCELLED" && hasResults) ||
    (status === "IN_PROGRESS" && canShowInProgress && hasResults) ||
    hasFulfillmentNotes
  );
}
